import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Plus, Minus, X, UtensilsCrossed } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { API_BASE, idem } from '@/utils/api';
import { money } from '@/components/tables/tableStates';
import { thumbUrl } from '../shared';

export const ProductPhoto = ({ url, alt, className = '' }) => (url
  ? <img src={thumbUrl(url)} alt={alt} loading="lazy" decoding="async" className={`m-photo ${className}`} />
  : <span className={`m-photo grid place-items-center text-muted-foreground ${className}`}><UtensilsCrossed className="w-6 h-6" /></span>);

// Toma de pedidos del personal (Moderno): categorías, buscador, grilla con fotos y pedido actual
export const OrderTaker = ({ sessionId, title, onClose, onSent }) => {
  const [menu, setMenu] = useState({ categories: [], products: [] });
  const [cat, setCat] = useState('all');
  const [q, setQ] = useState('');
  const [cart, setCart] = useState([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API_BASE}/menu`).then(r => setMenu(r.data)); }, []);
  const products = useMemo(() => (menu.products || []).filter(p => p.is_available !== false)
    .filter(p => cat === 'all' || p.category_id === cat).filter(p => !q || p.name.toLowerCase().includes(q.toLowerCase())), [menu, cat, q]);
  const add = (p, d = 1) => setCart(c => {
    const ex = c.find(i => i.product_id === p.product_id);
    if (!ex) return d > 0 ? [...c, { product_id: p.product_id, name: p.name, price: p.price, quantity: 1, notes: '' }] : c;
    return c.map(i => i.product_id === p.product_id ? { ...i, quantity: i.quantity + d } : i).filter(i => i.quantity > 0);
  });
  const total = cart.reduce((a, i) => a + i.price * i.quantity, 0);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_BASE}/sessions/${sessionId}/orders`, {
        items: cart.map(i => ({ product_id: i.product_id, quantity: i.quantity, notes: i.notes || null })), notes: note || null,
      }, idem());
      if (data?.order_id && data.status === 'Nuevo') await axios.put(`${API_BASE}/orders/${data.order_id}/status`, { status: 'Confirmado' }).catch(() => {});
      toast.success('Pedido enviado a cocina');
      onSent?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo enviar'); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-background flex flex-col md:flex-row" role="dialog" aria-label={`Pedido ${title}`} data-testid="order-taker">
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="flex items-center gap-3 p-4 border-b border-border">
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Cerrar" data-testid="order-taker-close"><X className="w-5 h-5" /></Button>
          <h2 className="text-xl font-bold">Nuevo pedido · {title}</h2>
          <div className="relative ml-auto w-full max-w-xs hidden sm:block">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder="Buscar producto" value={q} onChange={e => setQ(e.target.value)} data-testid="order-taker-search" />
          </div>
        </header>
        <div className="flex gap-2 overflow-x-auto px-4 py-3" role="tablist">
          {[{ category_id: 'all', name: 'Todo' }, ...(menu.categories || [])].map(c => (
            <button key={c.category_id} role="tab" aria-selected={cat === c.category_id} onClick={() => setCat(c.category_id)} data-testid={`order-cat-${c.category_id}`}
              className={`shrink-0 h-9 px-4 rounded-full text-sm font-semibold ${cat === c.category_id ? 'bg-[#2563EB] text-white' : 'bg-muted'}`}>{c.name}</button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto px-4 pb-4 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 content-start">
          {products.map(p => (
            <button key={p.product_id} onClick={() => add(p)} className="m-card overflow-hidden text-left hover:ring-2 hover:ring-[#2563EB] transition-shadow" data-testid={`order-product-${p.product_id}`}>
              <ProductPhoto url={p.image_url} alt={p.name} className="w-full h-28" />
              <div className="p-2.5"><div className="font-semibold text-sm leading-tight line-clamp-2">{p.name}</div><div className="font-bold mt-1">{money(p.price)}</div></div>
            </button>
          ))}
        </div>
      </div>
      <aside className="md:w-[360px] border-t md:border-t-0 md:border-l border-border bg-card flex flex-col max-h-[45vh] md:max-h-none" data-testid="order-taker-cart">
        <h3 className="px-4 pt-4 font-bold">Pedido actual</h3>
        <ul className="flex-1 overflow-y-auto px-4 divide-y divide-border">
          {cart.map(i => (
            <li key={i.product_id} className="py-2 space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="flex-1 text-sm font-medium">{i.name}</span>
                <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => add(i, -1)} aria-label="Menos" data-testid={`cart-minus-${i.product_id}`}><Minus className="w-3 h-3" /></Button>
                <span className="w-5 text-center font-bold">{i.quantity}</span>
                <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => add(i, 1)} aria-label="Más" data-testid={`cart-plus-${i.product_id}`}><Plus className="w-3 h-3" /></Button>
              </div>
              <Input className="h-8 text-xs" placeholder="Nota (ej. sin cebolla)" value={i.notes}
                onChange={e => setCart(c => c.map(x => x.product_id === i.product_id ? { ...x, notes: e.target.value } : x))} data-testid={`cart-note-${i.product_id}`} />
            </li>
          ))}
          {!cart.length && <li className="py-6 text-sm text-muted-foreground text-center">Tocá un producto para agregarlo</li>}
        </ul>
        <div className="p-4 space-y-2 border-t border-border">
          <Input placeholder="Nota general del pedido" value={note} onChange={e => setNote(e.target.value)} data-testid="order-general-note" />
          <div className="flex justify-between font-bold text-lg"><span>Total</span><span>{money(total)}</span></div>
          <Button className="w-full h-12 bg-[#2563EB] hover:bg-[#1d4ed8] text-white" disabled={!cart.length || busy} onClick={send} data-testid="order-send-btn">Enviar a cocina</Button>
        </div>
      </aside>
    </div>
  );
};
